import { useState, useEffect } from "react";
import { Form, useLoaderData, useActionData, useNavigation, useFetcher } from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button, LinkButton } from "../../components/common";
import TierManagement from "../../components/networks/TierManagement";
import { useAuth } from "../../hooks/useAuth";
import { MEMBERSHIP_TYPES } from "../../schemas";
import type { NetworkDetailData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import styles from "./NetworkSettingsPage.module.css";

export default function NetworkSettingsPage() {
  const { network, tiers, organizationId } = useLoaderData() as NetworkDetailData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const deleteFetcher = useFetcher();
  const { user } = useAuth();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [membershipType, setMembershipType] = useState(network.membershipType);

  const isSubmitting = navigation.state === "submitting";
  const isDeleting = deleteFetcher.state === "submitting";

  const userOrg = user?.organizations.find((o) => o.id === organizationId);
  const userRole = userOrg?.role || null;
  const canDelete = userRole === "owner" || userRole === "admin";

  useEffect(() => {
    if (!actionData) return;

    if (actionData.success) {
      toast.success("Network settings updated successfully!");
    } else if (actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  useEffect(() => {
    if (deleteFetcher.data) {
      const result = deleteFetcher.data as ActionResult;
      if (result.error) {
        toast.error(result.error);
        setShowDeleteConfirm(false);
      }
    }
  }, [deleteFetcher.data]);

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.container}>
          <div className={styles.header}>
            <h1>Network Settings</h1>
            <LinkButton to={`/organizations/${organizationId}/networks`} variant="secondary">
              Back to Networks
            </LinkButton>
          </div>

          <div className={styles.section}>
            <h2>General Settings</h2>
            <Form method="post" className={styles.form}>
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.name ? styles.formGroupError : ""}`}
              >
                <label htmlFor="name">Network Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  defaultValue={network.name}
                  placeholder="My Podcast Network"
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.name && (
                  <span className={styles.formError}>{actionData.fieldErrors.name}</span>
                )}
              </div>

              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.slug ? styles.formGroupError : ""}`}
              >
                <label htmlFor="slug">URL Slug</label>
                <input
                  id="slug"
                  name="slug"
                  type="text"
                  defaultValue={network.slug}
                  placeholder="my-podcast-network"
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.slug && (
                  <span className={styles.formError}>{actionData.fieldErrors.slug}</span>
                )}
                <small className={styles.helpText}>
                  Only lowercase letters, numbers, and hyphens allowed.
                </small>
              </div>

              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.description ? styles.formGroupError : ""}`}
              >
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  defaultValue={network.description || ""}
                  placeholder="Describe what this network is about..."
                  rows={4}
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.description && (
                  <span className={styles.formError}>{actionData.fieldErrors.description}</span>
                )}
              </div>

              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.logoUrl ? styles.formGroupError : ""}`}
              >
                <label htmlFor="logoUrl">Logo URL</label>
                <div className={styles.artworkField}>
                  {network.logoUrl && (
                    <img
                      src={network.logoUrl}
                      alt="Current logo"
                      className={styles.artworkPreview}
                    />
                  )}
                  <input
                    id="logoUrl"
                    name="logoUrl"
                    type="url"
                    defaultValue={network.logoUrl || ""}
                    placeholder="https://example.com/logo.png"
                    disabled={isSubmitting}
                  />
                </div>
                {actionData?.fieldErrors?.logoUrl && (
                  <span className={styles.formError}>{actionData.fieldErrors.logoUrl}</span>
                )}
                <small className={styles.helpText}>
                  Recommended: square image, at least 500x500px.
                </small>
              </div>

              <div className={styles.formGroup}>
                <span className={styles.fieldLabel}>Membership Type</span>
                <div className={styles.membershipOptions}>
                  {MEMBERSHIP_TYPES.map((type) => (
                    <label
                      key={type.value}
                      aria-label={type.label}
                      className={`${styles.membershipOption} ${membershipType === type.value ? styles.selected : ""}`}
                    >
                      <input
                        type="radio"
                        name="membershipType"
                        value={type.value}
                        checked={membershipType === type.value}
                        onChange={(e) => setMembershipType(e.target.value as typeof membershipType)}
                        disabled={isSubmitting}
                      />
                      <div className={styles.membershipOptionContent}>
                        <span className={styles.membershipOptionLabel}>{type.label}</span>
                        <span className={styles.membershipOptionDescription}>
                          {type.description}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.formActions}>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  loadingText="Saving..."
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          </div>

          <div className={styles.section}>
            <h2>Members</h2>
            <p
              style={{
                margin: "0 0 var(--spacing-4) 0",
                color: "var(--color-gray-600)",
                fontSize: "var(--font-size-sm)",
              }}
            >
              Manage podcast memberships, invitations, and revenue splits.
            </p>
            <LinkButton
              to={`/organizations/${organizationId}/networks/${network.id}/members`}
              variant="primary"
            >
              Manage Members
            </LinkButton>
          </div>

          <TierManagement organizationId={organizationId} networkId={network.id} tiers={tiers} />

          {canDelete && (
            <div className={`${styles.section} ${styles.dangerZone}`}>
              <h2>Danger Zone</h2>
              <div className={styles.dangerContent}>
                <div>
                  <h3>Delete Network</h3>
                  <p>
                    Once you delete a network, there is no going back. This will permanently delete
                    the network and remove all member podcasts from it.
                  </p>
                </div>
                {!showDeleteConfirm ? (
                  <Button type="button" variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                    Delete Network
                  </Button>
                ) : (
                  <div className={styles.deleteConfirm}>
                    <p className={styles.confirmText}>
                      Are you sure? This action cannot be undone.
                    </p>
                    <div className={styles.confirmActions}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                      >
                        Cancel
                      </Button>
                      <deleteFetcher.Form
                        method="post"
                        action={`/organizations/${organizationId}/networks/${network.id}/delete`}
                      >
                        <Button
                          type="submit"
                          variant="danger"
                          loading={isDeleting}
                          loadingText="Deleting..."
                        >
                          Yes, Delete Forever
                        </Button>
                      </deleteFetcher.Form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
